import { Col, Label, Row, Switch, TextArea, useToast } from "@commonlibs";
import HalfFormRow from "ui/common/components/input/Form/HalfFormRow/HalfFormRow";
import ImageUploadLabeled from "ui/common/components/input/ImageUpload/ImageUploadLabeled/ImageUploadLabeled";
import TextField from "ui/common/components/input/TextField/TextField";
import useTranslate from "ui/locale/hooks/useTranslate";
import { useMutation } from "react-query";
import { giftcardsApi } from "features/giftcards/api/giftcardsApi";
import { AddDenominationButton, CheckboxRoot } from "./GiftCardForm.styles";
import { CheckboxGroup } from "ui/common/components/input/CheckboxGroup/CheckboxGroup";
import { useState } from "react";
import { GiftCardFormDto, GiftCardFormDtoKey } from "ui/giftcards/schemas/CreateGiftCard.schema";
import { useFormatNumber } from "ui/common/hooks/useFormatNumber";
import PartnerAutocomplete from "ui/partner/container/PartnerAutocomplete/PartnerAutocomplete";
import type { PartnerDto } from "common/models/api/models/PartnerDto";
import GiftCardCategoriesAutocomplete from "../components/GiftCardCategoriesAutocomplete/GiftCardCategoriesAutocomplete";
import { GiftCardStockStatus } from "common/models/api/models/GiftCardStock";

function GiftCardForm({
    form,
    onChange,
}: {
    form: GiftCardFormDto;
    onChange: (key: GiftCardFormDtoKey, value: string | number[] | undefined) => void;
}) {
    const translate = useTranslate();
    const formatNumber = useFormatNumber();
    const { showToast } = useToast();
    const [newDenomination, setNewDenomination] = useState("");
    const { mutateAsync: uploadLogo } = useMutation((file: File) => giftcardsApi.uploadLogo(file).then((res) => res.data));

    const handleLogoError = (error: unknown) => {
        const message = (error as { response?: { data?: { message?: string } } })?.response?.data?.message;
        showToast(message ?? translate("unexpectedError"), { type: "error" });
    };

    const defaultGiftCardDenominations = new Set([
        20000,
        30000,
        40000,
        50000,
        60000,
        80000,
        100000,
        120000,
        150000,
        200000,
        250000,
        300000,
        ...form.denominations,
    ]);

    const [giftCardDenominations, setGiftCardDenominations] = useState([...defaultGiftCardDenominations].sort((a, b) => a - b));

    function onAddDenomination() {
        if (!newDenomination) return;
        const newDenominationInt = Number(newDenomination);
        if (!isNaN(newDenominationInt) && newDenominationInt > 0 && !giftCardDenominations.includes(newDenominationInt)) {
            setGiftCardDenominations([...giftCardDenominations, newDenominationInt]);
            setNewDenomination("");
        }
    }

    function onDenominationsChange(selectedValues: number[]) {
        onChange("denominations", selectedValues);
    }

    const handlePartnerChange = (e: unknown) => {
        const partner = e as PartnerDto;
        onChange("partner", partner.address);
    };

    const handleKeyDown = (event: React.KeyboardEvent) => {
        if (event.key === "Enter") {
            onAddDenomination();
        }
    };

    return (
        <Col gap={17}>
            <HalfFormRow>
                <PartnerAutocomplete
                    style={{ width: "100%" }}
                    name="partner"
                    label={translate("giftCardPartnerName")}
                    placeholder={translate("giftCardPartnerName")}
                    required
                    defaultSearch={form.partner}
                    value={form.partner}
                    searchArchived={false}
                    onChange={handlePartnerChange}
                    partnerType="giftcard"
                />
                <ImageUploadLabeled
                    onChange={(value) => onChange("imageUrl", value || "")}
                    alt="giftcard-image"
                    label={translate("giftCardImage")}
                    value={form.imageUrl || undefined}
                    uploadFn={uploadLogo}
                    onError={handleLogoError}
                />
            </HalfFormRow>
            <GiftCardCategoriesAutocomplete
                label={translate("category")}
                placeholder={translate("category")}
                defaultSearch={form.category}
                onChange={(value) => {
                    onChange("category", value);
                }}
                onSearchChange={(value) => {
                    onChange("category", value);
                }}
            />
            <TextArea
                onChange={(value) => onChange("termsAndConditions", value || "")}
                value={form.termsAndConditions}
                style={{ width: "100%", whiteSpace: "pre-wrap" }}
                label={translate("termsAndConditions")}
                required
                rows={6}
            ></TextArea>
            {form.id && (
                <TextField
                    css={{ flex: 1, color: "#27292b important" }}
                    label={translate("agreementCode")}
                    placeholder={translate("agreementCode")}
                    onChange={(value) => {
                        const agreementCodeValue = value === "" ? undefined : value;
                        onChange("agreementCode", agreementCodeValue);
                    }}
                    value={form.agreementCode || ""}
                />
            )}
            {!form.id && (
                <Col gap={7}>
                    <Label variant="body2Strong" label={translate("giftCardType")} />
                    <Switch
                        value={form.type === GiftCardStockStatus.onDemand}
                        onChange={(ev) => onChange("type", ev ? GiftCardStockStatus.onDemand : GiftCardStockStatus.stock)}
                    />
                </Col>
            )}
            {form.type === GiftCardStockStatus.onDemand && (
                <>
                    <Col gap={7}>
                        <Label variant="body2Strong" label={translate("giftCardValue")} />
                        <CheckboxGroup
                            options={giftCardDenominations.map((item) => ({
                                label: formatNumber(item, { useGrouping: true }),
                                value: item,
                            }))}
                            value={form.denominations}
                            onChange={onDenominationsChange}
                            gap={25}
                            direction="row"
                            CheckboxComponent={CheckboxRoot}
                        />
                    </Col>
                    <Col gap={7}>
                        <Label variant="body2Strong" label={translate("manualValue")} />
                        <Row gap={10} alignItems="center">
                            <TextField
                                value={newDenomination}
                                onChange={setNewDenomination}
                                css={{ flex: 1, color: "#27292b important" }}
                                validators={{ number: true }}
                                placeholder={translate("manualValue")}
                                onKeyDown={handleKeyDown}
                            />
                            <AddDenominationButton onClick={onAddDenomination}>{translate("addNewValue")}</AddDenominationButton>
                        </Row>
                    </Col>
                    <TextField
                        css={{ flex: 1, color: "#27292b important" }}
                        label={translate("closingTime")}
                        placeholder={translate("closingTime")}
                        onChange={(value) => {
                            const closingTimeValue = value === "" ? undefined : value;
                            onChange("closingTime", closingTimeValue);
                        }}
                        value={form.closingTime || ""}
                        type="time"
                    />
                </>
            )}
        </Col>
    );
}

export default GiftCardForm;
